import UnorderedListOutlined from '@ant-design/icons/lib/icons/UnorderedListOutlined'
import { ExpertThoughtsIcon } from 'Components/common/Icons/ExpertThoughts'
import ExpertThoughts, { ExpertThoughtsProps } from 'Components/tasks/ExpertThoughts/ExpertThoughts'
import TaskInstruction from 'Components/tasks/TaskInstruction/TaskInstruction'
import {
    DISMISSED_BADGE_NOTIFICATION_KEY,
    ErrorCode,
    EXPERTS_THOUGHTS_ITEM_ID,
    NavigationSource,
} from 'Constants/constants'
import { DEBOUNCE_INTERVALS } from 'Constants/page.constants.ts'
import { ContextHandlerFactory } from 'Core/ContextHandler/ContextHandlerFactory'
import { IContextHandler } from 'Core/ContextHandler/IContextHandler'
import { Studio } from 'Core/Studio'
import { ExpertsCommentsType, WorkbenchUiAccessorType } from 'Core/Workbench'
import {
    AssignmentResponseType,
    getJobPreview,
    saveTaskSubmission,
    submitTaskSubmission,
    TaskApiErrorResponse,
} from 'Services/tasks.service'
import {
    buildDummyDraftOutputContainer,
    fillProctoringTimeoutValues,
} from 'Utils/proctoringSubmit.utils'
import type { CSATFeedbackData } from 'Types/CSAT.types'
import { ExpertsAttributesType } from 'Types/Attribute.types'
import {
    ContainerDataType,
    ContainerType,
    AttributeDefinitionType,
    ConversationResponseDataType,
    DecisionStatusType,
    DefaultValueTypeDefinitions,
    QCAttributesData,
    StepTypes,
    TaskAttribute,
    TaskConfigType,
    StepType,
    UpdatedQCAttributesData,
    AssignmentType,
    TaskAdditionalInfo,
    ErrorAndWarningCount,
    NotificationPropsType,
    CommonAnalyticsObject,
    LinearConversationsDataType,
} from 'Types/Tasks.types'
import { getValueFromOldOrNewValueTypes } from 'Utils/common.utils.ts'
import { createSidebarIcon } from 'Utils/component.utils'
import { UpdatableReactElement } from 'shared/utils/react.utils'
import { debounce, isEmpty, capitalize } from 'lodash'
import message from 'antd/lib/message'
import { FormInstance } from 'antd/lib/form'
import Flex from 'antd/lib/flex'
import dayjs from 'dayjs'
import { ModalManager } from 'Core/ModalCore/ModalManager'
import { ModalContext } from 'Core/ModalCore/Modal'
import { ActionManager, ActionParam } from 'Core/ActionCore/ActionManager'
import { ActionContext } from 'Core/ActionCore/Action'
import { TaskState } from 'Core/TaskState'
import { IContainerStrategy } from 'Core/StateContainerCore/ContainerStrategy'
import { ValueTypeDefinitions } from 'Types/Tasks.types'
import React from 'react'
import styles from 'src/components/tasks/ExpertThoughts/ExpertThoughts.module.less'
import { AttributeManager } from 'Core/AttributeCore/AttributeManager'
import { CommonTaskUI } from './UI/CommonTaskUI'
import { TaskContextType } from 'Contexts/TaskContext'
import { isViewerMode } from 'Utils/task.utils'

interface SaveUserSubmissionApiErrorResponse {
    response: {
        data: {
            error: {
                code: string
                message: string
            }
        }
    }
}

export class CommonTask<T extends ContainerType, U> {
    ui?: UpdatableReactElement
    isExiting?: boolean
    protected initialConfig: TaskConfigType<T, U>
    protected attributesForm?: FormInstance
    protected attributesUI?: UpdatableReactElement
    protected qcAttributesForm?: FormInstance
    isReadOnly: boolean = false
    protected attributesOnUI: { [key: string]: 1 } = {}
    contextHandler: IContextHandler<T, U>
    protected modalManager: ModalManager | undefined
    actionManager: ActionManager | undefined
    taskState: TaskState<T, U>
    attributeManager?: AttributeManager
    protected isSavedTask?: boolean

    constructor(
        protected studio: Studio,
        containerStrategy: IContainerStrategy<T, U>
    ) {
        this.initialConfig = this.studio.taskConfig as TaskConfigType<T, U>
        this.taskState = new TaskState<T, U>(containerStrategy)
        this.taskState.initTaskState(this.initialConfig as TaskConfigType<T, U>)
        this.saveTask = this.saveTask.bind(this)

        this.contextHandler = ContextHandlerFactory.createFor<T, U>(
            this.studio.studioConfig.context.type,
            {
                getStudio: () => this.studio,
                getTaskState: () => this.taskState.getCurrentState(),
                saveTask: this.saveTask,
                actionFunctions: this.getActionFunctions(),
                updateAttributesUI: this.updateAttributesUI,
                setReadOnly: this.setReadOnly,
                flushPendingAutoSave: this.flushPendingAutoSave,
                getActionManager: () => this.actionManager,
            }
        )
        this.studio.workbench.enqueueUiUpdate(() => {
            this.initializeAttributeManager()
            this.initializeModalManager()
            this.initializeActionManager()
        })
        this.isReadOnly = this.contextHandler.isReadOnly()
        this.isSavedTask = this.isSavedTaskFromUrl()
    }

    public destroy = () => {}

    initializeAttributeManager = () => {
        this.attributeManager = new AttributeManager({
            taskConfig: this.initialConfig as TaskConfigType<ContainerType, unknown>,
            taskState: this.taskState as unknown as TaskState<ContainerType, unknown>,
            studio: this.studio,
            saveTask: this.autoSaveTask,
            attributesOnUI: this.attributesOnUI,
            getCommonAnalyticsObject: this.getCommonAnalyticsObject,
            task: this as unknown as CommonTask<ContainerType, unknown>,
        })
    }

    initializeModalManager = () => {
        const modalContext: ModalContext = {
            task: this as unknown as CommonTask<ContainerType, unknown>,
            studio: this.studio,
            workbenchUiAccessor: this.studio.workbench.workbenchUiAccessor,
            taskAdditionalInfo: this.getTaskAdditionalInfo(),
            errorAndWarningCount: this.getErrorAndWarningCount(),
            timeElapsedInSeconds: this.studio.taskManager.timer.getElapsedTime,
            isRejected:
                this.initialConfig.assignment.task.output_start_state_container?.decision
                    ?.status === 'REJECT',
            isAIUsageConfirmationEnabled:
                !!this.studio.studioConfig.featureFlags?.is_ai_usage_confirmation_enabled,
            contextHandler: this.contextHandler as IContextHandler<ContainerType, unknown>,
        }
        this.modalManager = new ModalManager(modalContext)
        this.modalManager.useModals(this.contextHandler.getDefaultModals())
    }

    initializeActionManager = () => {
        const actionContext: ActionContext = {
            studio: this.studio,
            task: this as unknown as CommonTask<ContainerType, unknown>,
            workbenchUiAccessor: this.studio.workbench.workbenchUiAccessor!,
            modalManager: this.modalManager!,
        }
        this.actionManager = new ActionManager(actionContext)
    }

    shouldExitTask = () => {
        return this.isExiting || this.isSavedTask
    }

    navigateTask = (notification?: NotificationPropsType) => {
        if (this.shouldExitTask()) {
            this.studio.exitTask(notification)
        } else {
            this.studio.nextTask(notification)
        }
    }

    getTaskAdditionalInfo = () => {
        const taskConfig = this.initialConfig
        const taskAdditionalInfo: TaskAdditionalInfo = {
            taskId: taskConfig.assignment.task.id,
            projectId: taskConfig.assignment.project_id,
            assignedUserId: taskConfig.assignment.user_id,
            stepId: taskConfig.assignment.step?.id,
            useCase: taskConfig.assignment.task.type,
        }
        return taskAdditionalInfo
    }

    public setReadOnly(isReadOnly: boolean) {
        this.isReadOnly = isReadOnly
    }

    private isSavedTaskFromUrl(): boolean {
        const searchParams = new URLSearchParams(window.location.search)
        return searchParams.get('source') === NavigationSource.SAVED_TASKS
    }

    private getActionFunctions = () => {
        return {
            addMakerActions: this.addMakerActions.bind(this),
            addQCActions: this.addQCActions.bind(this),
            addReviewerActions: this.addReviewerActions.bind(this),
            addTriagerActions: this.addTriagerActions.bind(this),
        }
    }

    getModalManager(): ModalManager | undefined {
        return this.modalManager
    }

    protected keyBindingActions = (event: KeyboardEvent) => {
        if (event.key === 's' && (event.ctrlKey || event.metaKey)) {
            event.preventDefault()
            console.log('Save command triggered')
            this.saveTask(true)
        }
    }

    showNotification = (message: string, level: 'error' | 'warning' | 'info') => {
        this.studio.workbench.enqueueUiUpdate(ui => {
            ui.notifier.notification[level]({ message })
        })
    }

    getCommonAnalyticsObject = (): CommonAnalyticsObject => {
        const assignmentObject = this.initialConfig.assignment
        return {
            project_id: assignmentObject.project_id,
            task_id: assignmentObject.task.id,
            user_id: assignmentObject.user_id,
            step_id: assignmentObject.step?.id,
        }
    }

    protected onEnableEditResponse() {
        this.updateAttributesUI()
    }

    protected autoSaveTask = debounce(
        async (options?: { onComplete?: (props?: NotificationPropsType) => void }) =>
            this.saveTask(false, options),
        DEBOUNCE_INTERVALS.AUTO_SAVE_TASK
    )

    flushPendingAutoSave = async (): Promise<void> => {
        await this.autoSaveTask.flush()
    }

    getTaskContext = () => {
        return {
            studio: this.studio,
            taskAdditionalInfo: this.getTaskAdditionalInfo(),
            isReadOnly: () => this.isReadOnly,
            getAttributeManager: () => this.attributeManager,
            getInputDetails: () =>
                this.studio.taskConfig.assignment.task.input_container as ContainerDataType<
                    ContainerType.LinearConversation,
                    LinearConversationsDataType
                >,
            taskId: this.initialConfig.assignment.task.id,
        }
    }

    protected addCommonWorkbenchComponents = (
        workbenchUiAccessor: WorkbenchUiAccessorType,
        config: TaskConfigType<T, U>
    ) => {
        const tasUi = React.createElement(
            CommonTaskUI,
            {
                keyBindingActions: this.keyBindingActions,
            },
            this.ui?.element
        )

        workbenchUiAccessor.setTaskContextGetter({
            getTaskContext: this.getTaskContext as () => TaskContextType,
        })
        workbenchUiAccessor.replaceMainContent(tasUi)

        // add left sidebar items
        this.contextHandler.addLeftSidebarItems(workbenchUiAccessor)

        // add actions
        this.contextHandler.addActions(config)

        // add timer and other header elements
        this.contextHandler.addInfoElements(workbenchUiAccessor)
        this.contextHandler.startTimer()

        this.studio.taskManager.startTaskActivityMonitoring()

        this.studio.taskManager.setPromptContent(
            config.assignment.task.input_container.data.data.conversation_nodes?.at(-1)?.content
                .data
        )
        this.createExpertsThought(workbenchUiAccessor, config)
    }

    validateData = async () => {
        return this.attributeManager?.validateGroups()
    }

    async saveTask(
        explicit: boolean,
        options?: {
            onComplete?: (props?: NotificationPropsType) => void
        }
    ) {
        const taskState = this.taskState.getCurrentState()
        try {
            const draftOutputContainer = this.taskState.getModifiedDraftOutputContainer(
                this.initialConfig as TaskConfigType<T, U>
            )
            if (!taskState || !draftOutputContainer) {
                console.error('Cannot save task: task state is not initialized')
                if (explicit) {
                    this.showNotification(
                        'Cannot save task: task state is not initialized',
                        'error'
                    )
                }
                return
            }

            // Skip API call for preview/viewer modes
            if (isViewerMode(this.initialConfig.mode)) {
                return
            }

            await saveTaskSubmission({
                userId: taskState.user_id,
                submissionId: taskState.submission.id,
                timeElapsedInSeconds: Math.floor(
                    this.studio.taskManager.timer.getElapsedTime() / 1000
                ),
                draftOutputContainer,
            })
            if (explicit) {
                // We are using function saveTask for key bindings as well as the UI element 'Exit'.
                // In case of key bindings, we don't want the user to be redirected to home page on success.
                // Hence, only notification is shown in case of key bindings.
                if (options?.onComplete) {
                    options?.onComplete?.({ type: 'success', message: 'Task saved successfully' })
                } else {
                    this.showNotification('Task saved successfully', 'info')
                }
            }
        } catch (e) {
            if (explicit) {
                this.showNotification((e as Error).message, 'error')
            }
            console.error('Error saving task:', e)

            const errorMessage = (e as Error).message
            const err = e as SaveUserSubmissionApiErrorResponse
            const errorCode = err.response?.data?.error?.code

            this.contextHandler.handleSaveSubmissionError(
                errorMessage,
                errorCode,
                options?.onComplete
            )
        }
    }

    updateAttributesUI = () => {
        this.attributesUI?.updater.forceUpdate()
        this.attributeManager?.renderAllGroups()
    }

    handleDecisionAndSupplementForStepType = (
        step: StepType,
        payload: { confidenceLevel?: string; comment: string }
    ) => {
        const draftOutputContainer = this.taskState.getDraftOutputContainer()
        if (
            [
                StepTypes.Maker,
                StepTypes.QualityCheck,
                StepTypes.ExternalQc,
                StepTypes.Rework,
            ].includes(step.type)
        ) {
            if (!draftOutputContainer?.supplement) {
                this.taskState.createSupplement(step.name, payload.comment, payload.confidenceLevel)
            } else {
                this.taskState.updateSupplement({
                    step_name: step.name,
                    confidence_level: payload.confidenceLevel,
                    comment: {
                        data: {
                            value: payload.comment,
                            value_type: 'STRING',
                        },
                    },
                })
            }
        } else if ([StepTypes.Reviewer, StepTypes.Triager].includes(step.type)) {
            if (!draftOutputContainer?.decision) {
                this.taskState.createDecision(step.type, payload.comment)
            } else {
                this.taskState.updateDecision({
                    step_name: step.type,
                    supporting_comment: {
                        data: {
                            value: payload.comment,
                            value_type: 'STRING',
                        },
                    },
                })
            }
        } else {
            console.error(
                'Handling not done for storing supplement or decision data for step type:',
                step.type
            )
        }
    }

    private getExpertsThought(
        outputContainer: ContainerDataType<T, U>,
        stepName: string
    ): ExpertsCommentsType {
        const thoughtComment = getValueFromOldOrNewValueTypes(
            outputContainer?.decision?.supporting_comment
        )
        const supplementComment = getValueFromOldOrNewValueTypes(
            outputContainer?.supplement?.comment
        )

        let thoughtMessage = ''
        if (thoughtComment) {
            thoughtMessage = thoughtComment
        } else if (supplementComment) {
            thoughtMessage = supplementComment
        }

        const rejectionReasons = outputContainer?.decision?.submission_reasons

        return {
            outputContainerId: outputContainer?.id,
            message: thoughtMessage,
            panelHeader: capitalize(stepName ?? 'Unknown Role'),
            rejectionReasons:
                rejectionReasons && rejectionReasons.length > 0 ? rejectionReasons : undefined,
        }
    }

    private createPanelHeader(
        title: string,
        isReworkThought: boolean,
        timestamp?: string,
        isNewThought?: boolean
    ) {
        return React.createElement(Flex, {
            vertical: true,
            gap: 2,
            children: [
                React.createElement(Flex, {
                    gap: 7,
                    children: [
                        React.createElement(
                            'div',
                            { className: styles['panel-header-title'] },
                            title
                        ),
                        isReworkThought &&
                            React.createElement(
                                'div',
                                { className: styles['rework-feedback-tag'] },
                                'Rework'
                            ),
                        isNewThought &&
                            React.createElement(
                                'div',
                                { className: styles['new-feedback-tag'] },
                                'NEW'
                            ),
                    ],
                }),
                timestamp &&
                    React.createElement(
                        'div',
                        { className: styles['feedback-time'] },
                        dayjs(timestamp).format('hh:mm A, Do MMM YYYY')
                    ),
            ],
        })
    }

    private getThoughtBannerInfo = () => {
        return isViewerMode(this.initialConfig.mode)
            ? 'Please go through the below comment’s from experts.'
            : 'Please go through the below comment’s from experts and make necessary changes to the response'
    }

    private createExpertsThought(
        workbenchUiAccessor: WorkbenchUiAccessorType,
        config: TaskConfigType<T, U>
    ) {
        if (!config.assignment.output_history?.length) return
        const expertsThoughts: ExpertThoughtsProps['items'] = []
        const defaultActivePanel: 'first' | 'last' = 'first'
        const addThought = (
            message: string,
            attributesList: ExpertsAttributesType[],
            panelHeader: string,
            isReworkFeedback: boolean,
            feedbackCreatedAt?: string,
            isNewThought?: boolean,
            rejectionReasons?: string[]
        ) => {
            if (attributesList.length || message || rejectionReasons) {
                expertsThoughts.push({
                    message,
                    attributesList,
                    rejectionReasons,
                    panelHeader: this.createPanelHeader(
                        panelHeader,
                        isReworkFeedback,
                        feedbackCreatedAt,
                        isNewThought
                    ),
                })
            }
        }

        const processOutputs = (
            outputs: NonNullable<AssignmentType<T, U>['output_history']>,
            lastStepOutputContainerId?: string
        ) => {
            let isPreReworkThought = true
            outputs.forEach(output => {
                const feedback = this.getExpertsThought(output.output, output.step_name)
                const attributesList = this.getAttributesForFeedback(
                    config.assignment.task.attributes,
                    output.output
                )

                if (output.is_current_user_step) {
                    feedback.panelHeader = `${feedback.panelHeader} (you)`
                }

                const isNewThought =
                    !isViewerMode(config.mode) && lastStepOutputContainerId === output.output.id
                addThought(
                    feedback.message,
                    attributesList,
                    feedback.panelHeader,
                    !isPreReworkThought,
                    output.output.updated_at,
                    isNewThought,
                    feedback.rejectionReasons
                )

                if (
                    isPreReworkThought &&
                    (output.step_type === StepTypes.QualityCheck ||
                        output.step_type === StepTypes.ExternalQc)
                ) {
                    isPreReworkThought = false
                }
            })
        }

        if (isViewerMode(this.initialConfig.mode)) {
            const currentTaskId = config.assignment.task.id
            const currentTaskIndex = config.assignment.output_history.findIndex(
                task => task.task_id === currentTaskId
            )
            const outputs = config.assignment.output_history.slice(0, currentTaskIndex + 1)
            processOutputs(outputs)
        } else if (config.assignment.output_history) {
            const lastStepOutputContainerId =
                config.assignment.task.output_start_state_container?.id
            processOutputs(config.assignment.output_history, lastStepOutputContainerId)
        }
        expertsThoughts.reverse()

        if (expertsThoughts.length > 0) {
            const itemId = EXPERTS_THOUGHTS_ITEM_ID
            const highlightThoughts = (): boolean => {
                const isRejectedTask =
                    config.assignment.task.output_start_state_container?.decision?.status ===
                    DecisionStatusType.REJECT
                if (!isRejectedTask) return false
                const savedDismissed =
                    localStorage.getItem(DISMISSED_BADGE_NOTIFICATION_KEY) ?? '{}'
                const dismissed = JSON.parse(savedDismissed) ?? {}
                return !dismissed[config.assignment.task.id]?.includes(itemId)
            }

            const onPopoverClose = () => {
                this.studio.workbench.enqueueUiUpdate(workbenchUiAccessor => {
                    workbenchUiAccessor.leftSidebar.updateItem(itemId, {
                        id: itemId,
                        element: React.createElement(ExpertThoughts, {
                            items: expertsThoughts,
                            title: 'EXPERT’S THOUGHTS',
                            bannerInfo: this.getThoughtBannerInfo(),
                            defaultActivePanel,
                            highlightThoughts: false,
                        }),
                        icon: ExpertThoughtsIcon,
                        isElementHighlighted: false,
                        title: 'Expert’s Thoughts',
                    })
                })
            }
            const areThoughtsHighlighted = highlightThoughts()
            workbenchUiAccessor.leftSidebar.addItem({
                id: itemId,
                element: React.createElement(ExpertThoughts, {
                    items: expertsThoughts,
                    title: 'EXPERT’S THOUGHTS',
                    bannerInfo: this.getThoughtBannerInfo(),
                    defaultActivePanel,
                    highlightThoughts: areThoughtsHighlighted,
                    onPopoverClose,
                }),
                icon: ExpertThoughtsIcon,
                isElementHighlighted: areThoughtsHighlighted,
                title: 'Expert’s Thoughts',
            })
            !isViewerMode(config.mode) &&
                workbenchUiAccessor.notificationBadge.addNotificationBadge({
                    id: itemId,
                })
        }
    }

    protected getAttributesForFeedback = (
        taskAttributes: TaskAttribute[] | undefined,
        taskOutputStartData: ContainerDataType<T, U> | undefined
    ) => {
        const attributesList: ExpertsAttributesType[] = []

        const qcAttributes = taskAttributes?.filter(
            attribute => attribute.type === AttributeDefinitionType.QUALITY_EVALUATION
        )

        const qcAttributesValues = Object.values(
            (taskOutputStartData?.data.data as ConversationResponseDataType)?.quality_check
                ?.evaluation_on_response_version_id ?? {}
        )[0]

        if (isEmpty(qcAttributes) || isEmpty(qcAttributesValues)) {
            return attributesList
        }

        qcAttributes?.forEach(attribute => {
            if ((qcAttributesValues as QCAttributesData)?.[attribute.name]?.data?.value) {
                attributesList.push({
                    label: attribute.label,
                    value: (qcAttributesValues as QCAttributesData)[attribute.name].data.value,
                    config: attribute,
                })
            }

            if ((qcAttributesValues as UpdatedQCAttributesData)?.[attribute.name]?.value) {
                attributesList.push({
                    label: attribute.label,
                    value: (qcAttributesValues as UpdatedQCAttributesData)[attribute.name].value,
                    config: attribute,
                })
            }
        })

        return attributesList
    }

    protected getEmptyValueType(type: 'INT'): {
        data: {
            value: undefined
            value_type: 'INT'
        }
    }

    protected getEmptyValueType<T extends Exclude<keyof ValueTypeDefinitions, 'INT'>>(
        type: T
    ): {
        data: {
            value: ValueTypeDefinitions[T]
            value_type: T
        }
    }
    protected getEmptyValueType<T extends keyof ValueTypeDefinitions>(type: T) {
        if (type === 'INT') {
            return {
                data: {
                    value: undefined,
                    value_type: type,
                },
            }
        }

        return {
            data: {
                value: DefaultValueTypeDefinitions[type as Exclude<T, 'INT'>],
                value_type: type,
            },
        }
    }

    async submitTask(options?: {
        onComplete?: (props?: NotificationPropsType) => void
        feedbackData?: CSATFeedbackData
        useDummyIfEmpty?: boolean
    }) {
        try {
            await this.flushPendingAutoSave()
            const useDummyIfEmpty = !!options?.useDummyIfEmpty
            let draftOutputContainer

            try {
                draftOutputContainer = this.taskState.getModifiedDraftOutputContainer(
                    this.initialConfig as TaskConfigType<T, U>
                )
            } catch (draftError) {
                if (!useDummyIfEmpty) {
                    throw draftError
                }
                console.warn(
                    '[proctoring] duration-end could not read draft; using dummy payload',
                    {
                        submissionId: this.taskState.getCurrentState()?.submission.id,
                        stepType: this.initialConfig.assignment?.step?.type,
                        error: draftError,
                    }
                )
                draftOutputContainer = null
            }

            const taskState = this.taskState.getCurrentState()

            if (!taskState) {
                console.error('Cannot submit task: task state is not initialized')
                return
            }

            if (useDummyIfEmpty) {
                const attributeDefs = this.initialConfig.assignment?.task?.attributes
                draftOutputContainer = draftOutputContainer
                    ? (fillProctoringTimeoutValues(
                          draftOutputContainer,
                          attributeDefs
                      ) as typeof draftOutputContainer)
                    : (buildDummyDraftOutputContainer(
                          this.initialConfig.assignment,
                          this.taskState.getEmptyDraftContainer()
                      ) as typeof draftOutputContainer)
            }

            if (!draftOutputContainer) {
                console.error('Cannot submit task: draft output is not available')
                return
            }

            const pluginData = this.getPluginData()
            const submissionBase = {
                userId: taskState.user_id,
                submissionId: taskState.submission.id,
                timeElapsedInSeconds: useDummyIfEmpty
                    ? Math.ceil(this.studio.taskManager.timer.getElapsedTime() / 1000)
                    : Math.floor(this.studio.taskManager.timer.getElapsedTime() / 1000),
                pluginData,
                feedbackData: options?.feedbackData,
            }

            try {
                await submitTaskSubmission({
                    ...submissionBase,
                    draftOutputContainer,
                })
            } catch (submitError) {
                // Timer-end guarantee: if the real (already-filled) draft is rejected by
                // the backend, fall back once to a from-scratch dummy container so the
                // session still ends up submitted.
                if (!useDummyIfEmpty) {
                    throw submitError
                }
                console.warn(
                    '[proctoring] duration-end submit failed; retrying with from-scratch dummy payload',
                    {
                        submissionId: taskState.submission.id,
                        stepType: this.initialConfig.assignment?.step?.type,
                        error: submitError,
                    }
                )
                const dummyDraft = buildDummyDraftOutputContainer(
                    this.initialConfig.assignment,
                    this.taskState.getEmptyDraftContainer()
                ) as typeof draftOutputContainer
                await submitTaskSubmission({
                    ...submissionBase,
                    draftOutputContainer: dummyDraft,
                })
            }

            options?.onComplete?.({ type: 'success', message: 'Task submitted successfully' })
        } catch (e) {
            const error = e as TaskApiErrorResponse
            const errorCode = error.response?.data?.error?.code
            let errorMessage = error.response?.data?.error?.message ?? (e as Error).message

            if (
                errorCode === ErrorCode.SKIP_REASONS_CHANGED ||
                errorCode === ErrorCode.REJECTION_REASONS_CHANGED
            ) {
                errorMessage = `${errorMessage} Please refresh the page`
            }

            this.studio.workbench.enqueueUiUpdate(ui =>
                ui.notifier.notification.error({ message: errorMessage })
            )

            options?.onComplete?.({ type: 'error', message: errorMessage })
        }
    }

    protected addReviewerActions() {
        const reviewerActions: ActionParam[] = []
        const isSkipTaskConfigEnabled =
            this.initialConfig?.assignment.task.config.skip_task_config?.is_enabled
        if (isSkipTaskConfigEnabled) {
            reviewerActions.push({
                id: 'Skip',
                config: { title: this.isSavedTask ? 'Skip & Exit' : 'Skip', isCollapsed: false },
            })
        }

        reviewerActions.push({
            id: 'FinishReview',
            config: {
                title: this.isSavedTask ? 'Finish Review & Exit' : 'Finish Review',
                disabled: () => this.studio.inputManager.checkViolationByID('qc-attributes'),
            },
        })
        reviewerActions.push({ id: 'Exit' })
        this.actionManager?.addActions(reviewerActions)
    }

    addMakerActions() {
        const makerActions: ActionParam[] = []
        if (!this.isSavedTask) {
            makerActions.push({ id: 'Submit' })
        }
        const isSkipTaskConfigEnabled =
            this.initialConfig?.assignment.task.config.skip_task_config?.is_enabled
        if (isSkipTaskConfigEnabled) {
            makerActions.push({
                id: 'Skip',
                config: { title: this.isSavedTask ? 'Skip & Exit' : 'Skip' },
            })
        }
        makerActions.push({ id: 'SubmitAndExit', config: { isCollapsed: !this.isSavedTask } })
        makerActions.push({ id: 'Exit' })
        makerActions.push({
            id: 'SaveForLater',
            config: { title: this.isSavedTask ? 'Save For Later & Exit' : 'Save For Later' },
        })
        this.actionManager?.addActions(makerActions)
    }

    private async addQCActions() {
        const qcActions: ActionParam[] = []
        if (this.studio.studioConfig.featureFlags?.qc_editing) {
            const isSkipTaskConfigEnabled =
                this.initialConfig?.assignment.task.config.skip_task_config?.is_enabled
            if (isSkipTaskConfigEnabled) {
                qcActions.push({
                    id: 'Skip',
                    config: {
                        title: this.isSavedTask ? 'Skip & Exit' : 'Skip',
                        isCollapsed: false,
                    },
                })
            }
            qcActions.push({
                id: 'FinishQC',
                config: {
                    title: this.isSavedTask ? 'Finish QC & Exit' : 'Finish QC',
                    disabled: () => this.studio.inputManager.checkViolationByID('qc-attributes'),
                },
            })
        } else {
            qcActions.push({
                id: 'SubmitQC',
                config: {
                    title: this.isSavedTask ? 'Submit & Exit' : 'Submit',
                    disabled: () => this.studio.inputManager.checkViolationByID('qc-attributes'),
                },
            })
        }
        qcActions.push({ id: 'Exit' })
        qcActions.push({ id: 'SaveForLater' })
        this.actionManager?.addActions(qcActions)
    }

    private addTriagerActions() {
        const triagerActions: ActionParam[] = []
        const isSkipTaskConfigEnabled =
            this.initialConfig?.assignment.task.config.skip_task_config?.is_enabled
        if (isSkipTaskConfigEnabled) {
            triagerActions.push({
                id: 'Skip',
                config: { title: this.isSavedTask ? 'Skip & Exit' : 'Skip' },
            })
        }
        triagerActions.push({
            id: 'FinishTriaging',
            config: {
                title: this.isSavedTask ? 'Finish Triaging & Exit' : 'Finish Triaging',
                disabled: () => this.studio.inputManager.checkViolationByID('qc-attributes'),
            },
        })
        triagerActions.push({ id: 'Exit' })
        triagerActions.push({ id: 'SaveForLater' })
        this.actionManager?.addActions(triagerActions)
    }

    fetchJobPreview = async (
        jobId: string,
        stepType: string,
        options?: { onSuccess?: (response: AssignmentResponseType) => void }
    ) => {
        try {
            const previewDetails = await getJobPreview(jobId, stepType)
            if (!previewDetails) {
                message.error('Failed to fetch instructions. Please reload the page')
                return
            }

            const response: AssignmentResponseType = {
                assignmentResp: previewDetails.assignmentResp,
            }

            this.studio.workbench.enqueueUiUpdate(workbenchUi => {
                const TaskInstructionsComponent = React.createElement(TaskInstruction, {
                    instructions: response.assignmentResp.assignment.task.instructions,
                    title: this.contextHandler.getInstructionTitle(),
                })
                workbenchUi.leftSidebar.updateItem('task-instructions', {
                    id: 'task-instructions',
                    element: TaskInstructionsComponent,
                    icon: createSidebarIcon(UnorderedListOutlined),
                    title: this.contextHandler.getInstructionTitle(),
                })
            })

            options?.onSuccess?.(response)
            return response
        } catch (e) {
            this.studio.workbench.enqueueUiUpdate(ui =>
                ui.notifier.notification.error({ message: (e as Error).message })
            )
        }
    }

    protected getErrorAndWarningCount() {
        const errorAndWarningCount: ErrorAndWarningCount = {
            errorCount: Object.keys(this.studio.inputManager.getViolation().error).length,
            warningCount: Object.keys(this.studio.inputManager.getViolation().warning).length,
        }
        return errorAndWarningCount
    }

    createAttributeHeader = () => {
        return this.contextHandler.createAttributeHeader()
    }

    public shouldEnableViolations() {
        return this.contextHandler.shouldEnableViolations()
    }

    protected hasEditableAttributes(config: TaskConfigType<T, U>): boolean {
        return (
            config.assignment.task.attributes?.filter(
                item =>
                    [
                        AttributeDefinitionType.EXTRA_INFO,
                        AttributeDefinitionType.QUALITY_EVALUATION,
                    ].includes(item.type) && item.is_value_editable
            ).length > 0
        )
    }

    protected getEditableQCAttributes(attributes: TaskAttribute[]): TaskAttribute[] {
        return attributes.filter(
            attribute =>
                attribute.type === AttributeDefinitionType.QUALITY_EVALUATION &&
                attribute.is_value_editable
        )
    }

    protected getExtraInfoAttributes(config: TaskConfigType<T, U>) {
        return config.assignment.task.attributes.filter(
            attribute => attribute.type === AttributeDefinitionType.EXTRA_INFO
        )
    }

    getPluginData = () => {
        const plugins = this.studio.pluginManager.getInstalledPlugins()
        return plugins.reduce(
            (acc, plugin) => {
                const pluginData = plugin.getPluginData()
                if (pluginData) {
                    acc = { ...acc, ...pluginData }
                }
                return acc
            },
            {} as Record<string, unknown>
        )
    }
}
